/**
 * Tipos del dominio. Sin dependencias de React, red ni Firebase.
 * Referencia: ANALISIS-PLANILLAS.md §8 y PROMPT-MAESTRO §3.
 */

// ── Identificadores ───────────────────────────────────────────────────────
export type Id = string; // UUID v7 generado en el cliente

// ── Plantas ───────────────────────────────────────────────────────────────
export type CodigoPlanta = 'NV 17' | 'KM 13' | 'VALLE';

/**
 * Partida de hormigón que cobra cada planta por omisión.
 *
 * Es lo que aplicaba la importación del libro histórico, y ahora también la
 * carga manual: una guía sin partida no entra al estado de pago, y dejarla
 * «Sin asignar» por descuido era despachar sin cobrar.
 */
/**
 * Partidas de productos que no son hormigón y no dependen de la planta.
 *
 * La apertura de planta se cobra por día contra la 4.4. Sin esto, una guía
 * suya sin partida declarada caía en la partida de hormigón de su planta.
 */
export const ITEM_POR_PRODUCTO: Record<string, string> = {
  'APERTURA DE PLANTA': '4.4',
};

export const ITEM_POR_PLANTA: Record<CodigoPlanta, string> = {
  'NV 17': '1.8',
  'KM 13': '2.8',
  VALLE: '3.24',
};
export type Sector = 'INT MINA' | 'SUPERFICIE' | 'VALLE';

export interface Planta {
  id: Id;
  codigo: CodigoPlanta;
  nombre: string;
  sector: Sector;
  /** Carga mínima facturable. Bajo esto se genera recargo 4.3. KM 13/VALLE: 5 · NV 17: 2 */
  minCargaM3: number;
  activo: boolean;
}

/** Mapeo planta → sector (columna F de la hoja GUIAS del Excel). */
export const SECTOR_POR_PLANTA: Record<CodigoPlanta, Sector> = {
  'KM 13': 'SUPERFICIE',
  'NV 17': 'INT MINA',
  VALLE: 'VALLE',
};

// ── Turno ─────────────────────────────────────────────────────────────────
/** A = día · C = noche · TA = turno adicional (aparece 180 veces en KM 13). */
export type Turno = 'A' | 'C' | 'TA';

// ── Producto ──────────────────────────────────────────────────────────────
export type Grado = 'GN' | 'GB' | 'SH' | 'G';

export type FamiliaProducto = 'HORMIGON' | 'ARIDO' | 'CEMENTO' | 'ADITIVO' | 'SERVICIO' | 'OTRO';

export interface ProductoParseado {
  grado: Grado | null;
  mpa: number | null;
  pctConfianza: number | null;
  diametroArido: number | null;
  cono: number | null;
  /** AIR, R3, R7, IMP, ADI, FP3, FP5, FP6, FP0,8 … */
  extras: string[];
  /** false ⇒ requiere clasificación manual. NUNCA se descarta en silencio. */
  ok: boolean;
}

export interface Producto extends ProductoParseado {
  id: Id;
  codigo: string;
  codigoNormalizado: string;
  familia: FamiliaProducto;
  /** false para ARENA, TRASLADO, NULA, APERTURA DE PLANTA… */
  generaRecargos: boolean;
  activo: boolean;
}

import type { UnidadCantidad } from './unidades';

// ── Contrato ──────────────────────────────────────────────────────────────
export type UnidadItem =
  'M3' | 'UF/M3' | 'UF/KG' | 'TON' | 'MES' | 'GL' | 'UND' | 'UF/DIA' | 'UF/SACO' | 'B';

export interface ItemContrato {
  id: Id;
  capituloNumero: number;
  /** '1.12', '3.24', '4.5', '10.11' */
  codigo: string;
  descripcion: string;
  unidad: UnidadItem;
  cantidadContratada: number;
  /** Columna N del Excel: precio base de contrato. */
  precioBaseUf: number;
  /** false para las partidas marcadas «sin aumento» / «si aumento». */
  aplicaReajuste: boolean;
  esRecargo: boolean;
  activo: boolean;
}

export interface Capitulo {
  id: Id;
  numero: number;
  nombre: string;
  orden: number;
}

// ── Guía de despacho ──────────────────────────────────────────────────────
/**
 * De dónde cuelga la venta.
 *
 * `CONTRATO` es lo que se le cobra a CODELCO por el contrato vigente y entra
 * al estado de pago. `DIRECTA` es venta a un tercero fuera del contrato: se
 * registra igual —es despacho real, ocupa camión y planta— pero no se le
 * factura a CODELCO.
 */
export type TipoVenta = 'CONTRATO' | 'DIRECTA';

export type EstadoGuia =
  'BORRADOR' | 'EMITIDA' | 'ANULADA' | 'REQUIERE_CLASIFICACION' | 'FACTURADA';

/** Las 6 marcas de tiempo del ciclo, como instantes completos (no fracción de día). */
export interface MarcasGuia {
  salidaPlanta: Date;
  llegadaObra: Date;
  inicioDescarga: Date;
  terminoDescarga: Date;
  salidaObra: Date;
  llegadaPlanta: Date;
}

export type MarcasParciales = Partial<MarcasGuia>;

export interface Guia {
  id: Id;
  plantaId: Id;
  plantaCodigo: CodigoPlanta;
  folio: number;
  remito: number | null;
  /** Fecha operativa (día del despacho), en zona America/Santiago. */
  fecha: string; // yyyy-MM-dd
  turno: Turno | null;
  clienteId: Id | null;
  productoId: Id | null;
  cantidadM3: number;
  /** En qué se contó la cantidad: m³, sacos, kilos, toneladas o unidades. */
  unidadCantidad: UnidadCantidad;
  tipoVenta: TipoVenta;
  /** A qué estado de pago la asignó la planilla de origen, si lo declaró. */
  epDeclarado: number | null;
  /**
   * Histórico cargado para consultar: nunca entra a un estado de pago.
   *
   * Obligatorio a propósito. Estuvo como opcional y la pantalla del estado de
   * pago simplemente no lo pasaba: el dato se guardaba en la guía, el motor lo
   * miraba, y entre medio nadie lo llevaba. El compilador no avisó porque no
   * tenía por qué. Un campo que decide si algo se cobra no puede ser opcional.
   */
  soloConsulta: boolean;
  centroCostoId: Id | null;
  proyectoId: Id | null;
  destinoId: Id | null;
  destinoTexto: string | null;
  equipoId: Id | null;
  operadorMixerId: Id | null;
  operadorPlantaId: Id | null;
  solicitanteId: Id | null;
  observaciones: string | null;
  marcas: MarcasParciales;
  itemContratoId: Id | null;
  estado: EstadoGuia;
  motivoAnulacion: string | null;
  estadoPagoId: Id | null;
  origen: 'APP' | 'IMPORT_EXCEL' | 'INFORME_DIARIO';
}

// ── Tiempos derivados ─────────────────────────────────────────────────────
export interface TiemposGuia {
  trasladoIda: number | null;
  trasladoVuelta: number | null;
  trasladoTotal: number | null;
  espera: number | null;
  descarga: number | null;
  salidaObra: number | null;
  /** espera + descarga + salida obra (columna AF del Excel). */
  estadiaObra: number | null;
  /** max(0, estadía − 45) (columna AG). */
  sobreEstadia: number | null;
  /** round(sobreEstadía / 10, 1) (columna AH). */
  factor10Min: number | null;
  /** Regla Codelco: trunc(factor) + (frac ≥ 0,6 ? 1 : 0) (columna AI). */
  bloquesCodelco: number | null;
  /** Las 6 marcas presentes y coherentes. */
  completa: boolean;
  /** Anomalías detectadas (secuencia fuera de orden, duración absurda…). */
  anomalias: string[];
}

// ── Recargos ──────────────────────────────────────────────────────────────
export type ReglaRecargo =
  | 'CARGA_INCOMPLETA'
  | 'SOBREESTADIA'
  | 'BOMBEO_HN_A_HB'
  | 'AIRE_25_35'
  | 'AIRE_40_50'
  | 'DIF_CONO_HORMIGON'
  | 'DIF_CONO_SHOTCRETE'
  | 'FIBRA_APORTE_XTREME'
  | 'RESISTENCIA_R3'
  | 'RESISTENCIA_R7'
  | 'CONFIANZA_95'
  | 'IMPERMEABILIZANTE'
  | 'APERTURA_PLANTA';

export interface RecargoCalculado {
  regla: ReglaRecargo;
  /** Código de partida del contrato: '4.3', '4.5', '10.11'… */
  itemCodigo: string;
  cantidad: number;
  unidad: UnidadItem;
  precioUf: number;
  montoUf: number;
  detalle: string;
}

// ── Estado de pago ────────────────────────────────────────────────────────
export type EstadoEP = 'ABIERTO' | 'EN_REVISION' | 'CERRADO' | 'PRESENTADO' | 'APROBADO' | 'PAGADO';

export interface LineaEP {
  itemContratoId: Id;
  itemCodigo: string;
  descripcion: string;
  unidad: UnidadItem;
  capituloNumero: number;
  cantAcumAnterior: number;
  cantPeriodo: number;
  cantAcumFecha: number;
  /** Snapshot: si el precio cambia, los EP cerrados no se mueven. */
  precioUf: number;
  montoAcumAnteriorUf: number;
  montoPeriodoUf: number;
  montoAcumFechaUf: number;
  ajusteManual: number;
  motivoAjuste: string | null;
}

export interface TotalesEP {
  totalUf: number;
  valorUf: number;
  totalClp: number;
  ivaClp: number;
  totalFacturarClp: number;
  porCapitulo: { capituloNumero: number; nombre: string; montoUf: number }[];
}

export type NivelSemaforo = 'VERDE' | 'AMARILLO' | 'ROJO';

export interface ControlContrato {
  montoBaseUf: number;
  montoModificacionesUf: number;
  montoVigenteTotalUf: number;
  cobradoAcumuladoUf: number;
  saldoUf: number;
  pctConsumo: number;
  nivel: NivelSemaforo;
  excedido: boolean;
  excesoUf: number;
  excesoClp: number;
  mensaje: string;
}
